import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { InfoBoxRow } from 'components/InfoBox'
import { FlexDivRowCentered } from 'components/layout/flex'
import Spacer from 'components/Spacer'
import { Body, NumericValue } from 'components/Text'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModal } from 'state/app/reducer'
import {
	selectCrossMarginAccount,
	selectCrossMarginMarginInfo,
	selectMaintenanceMarginRatio,
} from 'state/futures/crossMargin/selectors'
import { RiskStatus } from 'state/futures/crossMargin/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import PencilButton from '../../../components/Button/PencilButton'

const TradeBalanceCrossMargin: React.FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { deviceType } = useWindowSize()
	const { ratio, riskStatus } = useAppSelector(selectMaintenanceMarginRatio)

	const { availableMargin, requiredMaintenanceMargin } = useAppSelector(selectCrossMarginMarginInfo)
	const crossMarginAccount = useAppSelector(selectCrossMarginAccount)

	const editButton = (
		<PencilButton
			width={11}
			height={11}
			onClick={(e) => {
				e.stopPropagation()
				dispatch(setOpenModal('futures_deposit_withdraw_cross_margin'))
			}}
		/>
	)

	return (
		<Container mobile={deviceType === 'mobile'}>
			<Header>
				<Body weight="bold" size="large">
					{t('futures.market.trade.margin.health')}
				</Body>
				<FlexDivRowCentered>
					<NumericValue
						weight="bold"
						size="large"
						color={
							riskStatus === 'high' ? 'negative' : riskStatus === 'low' ? 'positive' : 'preview'
						}
					>
						{formatPercent(ratio, { maxDecimals: 2 })}
					</NumericValue>
					<Spacer width={7} />
					<AccountStatusBadge $risk={riskStatus} />
				</FlexDivRowCentered>
			</Header>
			<MarginInfoSection>
				<InfoBoxRow
					title={t('futures.market.trade.trade-balance.available-margin')}
					textValue={formatDollars(availableMargin)}
					textValueIcon={crossMarginAccount ? editButton : null}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.trade-balance.maintenance-margin')}
					textValue={formatDollars(requiredMaintenanceMargin)}
				/>
			</MarginInfoSection>
		</Container>
	)
})

const Container = styled.div<{ mobile?: boolean }>`
	width: 100%;
	padding: 13px 15px;
	border-bottom: ${(props) => (props.mobile ? props.theme.colors.selectedTheme.border : 0)};
`

const Header = styled(FlexDivRowCentered)``

const MarginInfoSection = styled.div`
	padding-top: 10px;
`

const AccountStatusBadge = styled.div<{ $risk: RiskStatus }>`
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.riskStatus[props.$risk]};
	border-radius: 6px;
	height: 12px;
	width: 12px;
`

export default TradeBalanceCrossMargin
